import { useState } from 'react'
import { kycApi } from '../../../services/api-client'
import type { VendorKYC, BankingInfoData } from '../../../types'

interface Props {
  kyc: VendorKYC | null
  onComplete: (kyc: VendorKYC) => void
  onBack?: () => void
}

export function Step5Banking({ kyc, onComplete, onBack }: Props) {
  const [formData, setFormData] = useState<BankingInfoData>({
    bank_name: kyc?.bank_name || '',
    bank_account_holder_name: kyc?.bank_account_holder_name || '',
    iban: kyc?.iban || '',
    swift_bic: kyc?.swift_bic || '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const response = await kycApi.submitStep5(formData)
      onComplete(response.kyc)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al guardar información bancaria')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Información Bancaria
        </h2>
        <p className="text-gray-600">
          Proporcione los datos de su cuenta bancaria para recibir pagos.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <div className="space-y-6">
        <div>
          <label htmlFor="bank_name" className="block text-sm font-medium text-gray-700 mb-2">
            Nombre del Banco <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="bank_name"
            required
            value={formData.bank_name}
            onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Banco Nacional"
          />
        </div>

        <div>
          <label htmlFor="account_holder" className="block text-sm font-medium text-gray-700 mb-2">
            Titular de la Cuenta <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="account_holder"
            required
            value={formData.bank_account_holder_name}
            onChange={(e) => setFormData({ ...formData, bank_account_holder_name: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Nombre completo del titular"
          />
          <p className="text-sm text-gray-500 mt-1">
            Debe coincidir con el nombre en sus documentos de identidad
          </p>
        </div>

        <div>
          <label htmlFor="iban" className="block text-sm font-medium text-gray-700 mb-2">
            CLABE / IBAN <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="iban"
            required
            value={formData.iban}
            onChange={(e) => setFormData({ ...formData, iban: e.target.value.toUpperCase() })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
            placeholder="MX12345678901234567890"
            maxLength={34}
          />
          <p className="text-sm text-gray-500 mt-1">
            CLABE (México) o IBAN (internacional)
          </p>
        </div>

        <div>
          <label htmlFor="swift_bic" className="block text-sm font-medium text-gray-700 mb-2">
            Código SWIFT/BIC (opcional)
          </label>
          <input
            type="text"
            id="swift_bic"
            value={formData.swift_bic}
            onChange={(e) => setFormData({ ...formData, swift_bic: e.target.value.toUpperCase() })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
            placeholder="BOFMEXMM"
            maxLength={11}
          />
          <p className="text-sm text-gray-500 mt-1">
            Requerido solo para transferencias internacionales
          </p>
        </div>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-800">
              <strong>Seguridad:</strong> Tu información bancaria está encriptada y será utilizada únicamente para procesar pagos de tus ventas.
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-6 border-t">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-3 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            Atrás
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className={`px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${!onBack ? 'ml-auto' : ''}`}
        >
          {loading ? 'Guardando...' : 'Continuar'}
        </button>
      </div>
    </form>
  )
}
